import { useCallback, useEffect, useRef } from 'react';

import { CKEditor } from '@ckeditor/ckeditor5-react';

// Wrapper와 동일한 에디터 설정 사용 (HTML 렌더링 일관성 보장)
import { ClassicEditor, editorConfig } from '../CKEditorWrapper/ckeditor-custom-build';

import normalizeHtmlForCKEditor from '@shared/utils/normalizeHtmlForCKEditor';

import styles from './CKEditorViewer.module.scss';

/**
 * CKEditorViewer 컴포넌트
 * - CKEditor 5를 읽기 전용 모드로 표시하는 컴포넌트
 * - 툴바 없이 내용만 표시
 * - 품의서 상세 모달에서 사용
 */
export default function CKEditorViewer({ value, className, isPreview = false }) {
  const editorRef = useRef(null);

  // 에디터 준비 완료 시
  const handleReady = useCallback(
    (editor) => {
      editorRef.current = editor;
      // 읽기 전용 모드로 설정
      editor.enableReadOnlyMode('viewer');
      // 툴바 숨기기
      const toolbarElement = editor.ui.view.toolbar.element;
      if (toolbarElement) {
        toolbarElement.style.display = 'none';
      }

      // 미리보기 모드일 경우 에디터 루트 요소에 클래스 추가
      if (isPreview) {
        const viewRoot = editor.editing.view.document.getRoot();
        const domRoot = editor.editing.view.domRoots.get(viewRoot);
        if (domRoot) {
          domRoot.classList.add('is-preview');
        }
      }
    },
    [isPreview],
  );

  // value prop이 변경될 때 에디터 내용 업데이트
  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      const currentData = editorRef.current.getData();
      if (currentData !== value) {
        // 외부 HTML을 CKEditor 5가 안전하게 처리할 수 있도록 정규화
        // font-size 유지하여 폰트 크기 보존
        const normalizedValue = normalizeHtmlForCKEditor(value || '', {
          removeFontSize: false,
          stripNegativeTextIndent: true,
        });
        editorRef.current.setData(normalizedValue);
      }
    }
  }, [value]);

  // 초기 data도 정규화 (font-size 유지)
  const normalizedInitialData = normalizeHtmlForCKEditor(value || '', {
    removeFontSize: false,
    stripNegativeTextIndent: true,
  });

  // 뷰어 전용 설정: Wrapper와 동일한 설정 + 툴바만 비활성화
  const viewerConfig = {
    ...editorConfig,
    toolbar: [], // 툴바 없음
  };

  return (
    <div
      className={`${styles.editorViewer} ${isPreview ? styles.isPreview : ''} ${className || ''}`}
    >
      <CKEditor
        editor={ClassicEditor}
        data={normalizedInitialData}
        onReady={handleReady}
        config={viewerConfig}
        // disabled prop 제거: enableReadOnlyMode로 충분하며, disabled는 렌더링에 영향을 줄 수 있음
      />
    </div>
  );
}
